/**
 * NewForm design-system Badge.
 *
 * Pill-shaped status / category indicator. Renders the framework UI kit's
 * `.b` block 1:1 — Rethink Sans 12px / weight 500 / leading-none, 22px
 * height, rounded-full, sentence-case labels (not uppercase). The design
 * system reserves uppercase mono for kickers/eyebrows/table headers; pills
 * stay sans + sentence case.
 *
 *   import { Badge } from "@/components/ui/design-system/badge";
 *
 *   <Badge variant="ok">Active</Badge>
 *   <Badge variant="brand">New</Badge>
 *   <Badge variant="grad">Pro plan</Badge>
 *
 * Variants (matching the spec preview at
 * packages/nf-design-system/preview/components-badges.html):
 *
 *   default — dark navy background, white text (status: neutral primary)
 *   secondary — muted gray background, dark text (status: neutral default)
 *   outline — white background, hairline border (status: archived / neutral)
 *   ok — green tint (status: success, top performer)
 *   warn — amber tint (status: pending, capped, throttled)
 *   err — red tint (status: failed, error)
 *
 * The tonal variants (ok/warn/err/info/idle/brand-soft) resolve through
 * `--nf-ds-tone-*`, which carries a background and its own foreground and
 * flips under `.dark` in the token layer. Do not add `dark:` classes to them —
 * a tone that needs a different dark value is a token change, not a class.
 *   brand — solid NewForm purple (status: featured, new)
 *   brand-soft — purple tint (status: role/category — admin, staff)
 *   grad — purple→magenta gradient (status: premium / Pro plan)
 *
 * For icons, pass them as children alongside the label — the consumer
 * controls icon sizing. One leading icon; it names what kind of thing this
 * is, where the tone names how it stands.
 *
 * `dot` renders a status dot that inherits the variant's text colour, and
 * GUIDELINES.md now asks new work not to use it: the pill is already the
 * indicator, so a dot inside one is a second status light on a status light.
 * The prop stays for the 79 existing call sites. Beside plain text, with no
 * pill, the dot is the right control — that is `BADGE_DOT`, not this.
 */

import { cva, type VariantProps } from "class-variance-authority";
import type { HTMLAttributes } from "react";
import { cn } from "@/lib/utils";

const badgeVariants = cva(
  "inline-flex items-center gap-1.5 h-[22px] px-2.5 rounded-full font-nf-ds-sans text-xs font-medium leading-none whitespace-nowrap",
  {
    variants: {
      variant: {
        default: "bg-primary text-primary-foreground",
        secondary: "bg-secondary text-secondary-foreground",
        outline: "bg-background border border-border text-foreground",
        // Tonal variants come from DS tone tokens rather than Tailwind ramps.
        // Each carries its own foreground, and both flip under .dark from the
        // token layer — so there are no `dark:` classes to keep in sync here.
        ok: "bg-nf-ds-tone-ok-bg text-nf-ds-tone-ok-fg",
        warn: "bg-nf-ds-tone-warn-bg text-nf-ds-tone-warn-fg",
        err: "bg-nf-ds-tone-err-bg text-nf-ds-tone-err-fg",
        info: "bg-nf-ds-tone-info-bg text-nf-ds-tone-info-fg",
        idle: "bg-nf-ds-tone-neutral-bg text-nf-ds-tone-neutral-fg",
        brand: "bg-nf-ds-purple-product text-nf-ds-white",
        "brand-soft": "bg-nf-ds-tone-brand-bg text-nf-ds-tone-brand-fg",
        grad: "bg-gradient-to-r from-nf-ds-purple-product to-nf-ds-magenta text-nf-ds-white",
      },
    },
    defaultVariants: {
      variant: "default",
    },
  },
);

interface BadgeProps
  extends HTMLAttributes<HTMLSpanElement>,
    VariantProps<typeof badgeVariants> {
  /**
   * Render a small status dot (6px) before the children, in the variant's
   * text color. Use for "status with indicator" pills like "● Top performer".
   */
  dot?: boolean;
}

export function Badge({
  className,
  variant,
  dot,
  children,
  ...props
}: BadgeProps) {
  return (
    <span className={cn(badgeVariants({ variant }), className)} {...props}>
      {dot ? (
        <span aria-hidden="true" className="size-1.5 rounded-full bg-current" />
      ) : null}
      {children}
    </span>
  );
}

export { badgeVariants };
